import type { ConsentState, ConsentAction, ConsentLogPayload, CookieCategory } from "./cookie-consent.types";
import {
  CONSENT_COOKIE_NAME,
  CONSENT_VERSION,
  CONSENT_EVENT,
  CONSENT_MAX_AGE_DAYS,
} from "./cookie-consent.types";

const DEFAULT_STATE: ConsentState = {
  necessary: true,
  analytics: false,
  functional: false,
  marketing: false,
  timestamp: "",
  version: CONSENT_VERSION,
};

export const ALL_ACCEPTED: ConsentState = {
  necessary: true,
  analytics: true,
  functional: true,
  marketing: true,
  timestamp: "",
  version: CONSENT_VERSION,
};

export const ONLY_NECESSARY: ConsentState = {
  necessary: true,
  analytics: false,
  functional: false,
  marketing: false,
  timestamp: "",
  version: CONSENT_VERSION,
};

export const TOGGLABLE_CATEGORIES: CookieCategory[] = [
  "analytics",
  "functional",
  "marketing",
];

export const getConsentCookie = (): ConsentState | null => {
  if (typeof document === "undefined") return null;

  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${CONSENT_COOKIE_NAME}=`));

  if (!match) return null;

  try {
    const decoded = decodeURIComponent(match.split("=")[1]);
    const parsed = JSON.parse(decoded) as ConsentState;
    if (!parsed.timestamp) return null;
    return { ...DEFAULT_STATE, ...parsed };
  } catch {
    return null;
  }
};

export const setConsentCookie = (state: ConsentState): void => {
  if (typeof document === "undefined") return;

  const stamped: ConsentState = {
    ...state,
    timestamp: new Date().toISOString(),
    version: CONSENT_VERSION,
  };

  const maxAge = CONSENT_MAX_AGE_DAYS * 24 * 60 * 60;
  const value = encodeURIComponent(JSON.stringify(stamped));
  document.cookie = `${CONSENT_COOKIE_NAME}=${value}; path=/; max-age=${maxAge}; SameSite=Lax`;
};

export const clearConsentCookie = (): void => {
  if (typeof document === "undefined") return;
  document.cookie = `${CONSENT_COOKIE_NAME}=; path=/; max-age=0`;
};

export const dispatchConsentEvent = (state: ConsentState): void => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: state }));
};

export const hasCategory = (category: CookieCategory): boolean => {
  const consent = getConsentCookie();
  if (!consent) return category === "necessary";
  return consent[category] === true;
};

export const openCookiePreferences = (): void => {
  clearConsentCookie();
  dispatchConsentEvent({
    necessary: true,
    analytics: false,
    functional: false,
    marketing: false,
    timestamp: "",
    version: CONSENT_VERSION,
  });
};

export const sendConsentLog = async (
  apiUrl: string,
  state: ConsentState,
  action: ConsentAction,
  sourceApp: string,
): Promise<void> => {
  const payload: ConsentLogPayload = {
    categories: {
      necessary: state.necessary,
      analytics: state.analytics,
      functional: state.functional,
      marketing: state.marketing,
    },
    consent_version: state.version,
    source_app: sourceApp,
    action,
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
  };

  try {
    await fetch(`${apiUrl}/consent/log`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  } catch {
    // fire-and-forget
  }
};
